/*@flow*/
import type { Node, Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useContext, useState, Fragment } from 'react';
import numberFormat from 'underscore.string/numberFormat';
import dasherize from 'underscore.string/dasherize';
import Pie from './Pie';
// ----------
import * as t from 'io-ts';
import { ThrowReporter } from 'io-ts/lib/ThrowReporter';
// ----------
import './index.scss';
import cx from 'classnames';
import { sample, goTop } from './utils';
import FreeModal from 'comps/FreeModal';
import getAssetCompanyId from 'lib/getAssetCompanyId';
import cond from 'ramda/src/cond';
import compose from 'ramda/src/compose';
import T from 'ramda/src/T';
import head from 'ramda/src/head';
import { getNumericKeys } from './utils';
import isArray from 'lib/t/isArray';
import Context from './Context';
import { state } from 'ops';
import * as I from 'immutable';
import ErrorsView from 'comps/ErrorsView';
import useDownload from 'lib/useDownload';

const Str2Str = t.dictionary(t.string, t.string);
const Str2Any = t.dictionary(t.string, t.any);
const ViewConfig = t.type({
  title: t.string,
  headers: Str2Str,
  suffixes: Str2Str,
  order: t.array(t.string),
  subtitle: t.union([t.string, t.undefined]),
  data: t.array(Str2Any),
  activeHeader: t.union([t.string, t.undefined]),
  pie: t.union([t.boolean, t.undefined]),
  private: t.union([t.boolean, t.undefined]),
});
const ViewConfigArr = t.array(ViewConfig);
type ViewConfigArrT = any; // t.TypeOf<typeof ViewConfigArr>;
type ViewConfigT = any; // t.TypeOf<typeof ViewConfig>;
type Str2AnyT = any; // t.TypeOf<typeof Str2Any>;
type Str2StrT = any; // t.TypeOf<typeof Str2Str>;
type DataT = Str2AnyT[];
type SectionTableT = {
  data: DataT,
  headers: Str2StrT,
  order: string[],
  activeHeader: string,
};

const format = (datum: string | number) => {
  const datumType = typeof datum;
  if (datumType === 'string') {
    return datum;
  } else if (datumType === 'number') {
    // Indicates a floating point number.
    if (String(datum).match(/\./)) {
      return datum;
    }
    return numberFormat(datum);
  }
  return '-';
};

const properDash = (v, i) =>
  dasherize(v.toLowerCase().replace(/[^a-zA-Z\s0-9]+/g, '') + ' ' + i);

const SectionRow = (props: {
  data: Str2AnyT,
  order: string[],
  activeHeader: string,
  className?: string,
}) => (
  <div className={props.className || 'section-row'}>
    {props.order.map((k) => (
      <div key={k} className="section-cell">
        {format(props.data[k])}
      </div>
    ))}
  </div>
);
const SectionTable = (props: SectionTableT) => (
  <div className="section-table">
    <SectionRow
      data={props.headers}
      order={props.order}
      activeHeader={props.activeHeader}
      className="section-header"
    />
    {props.data.map((row, i) => (
      <SectionRow
        data={row}
        order={props.order}
        activeHeader={props.activeHeader}
        key={i}
      />
    ))}
  </div>
);
const SectionTitle = (props: { name: string }) =>
  props.name ? <h5>{props.name}</h5> : null;

// HACK: Harcoding a fallback width. Might be better if it were dynamic.
const WIDGET_WIDTH = window.innerWidth > 370 ? 370 : 275;

const subtract = (arr1: any[], arr2: any[]) =>
  arr1.filter((v) => !arr2.includes(v));
const pickRandomColor = (pickedColors = []) => {
  const COLORS = [
    '#a50026',
    '#d73027',
    '#f46d43',
    '#fdae61',
    '#fee090',
    '#ffffbf',
    '#e0f3f8',
    '#abd9e9',
    '#74add1',
    '#4575b4',
    '#313695',
  ];

  const color = sample(subtract(COLORS, pickedColors));
  return !color ? pickRandomColor() : color;
};
const getColors = (data: DataT) =>
  data.reduce((s, v) => {
    s[v.type] = pickRandomColor(Object.values(s));
    return s;
  }, {});

export const BackBtn = (): Element<string> => (
  <a className="go-back" onClick={goTop} title="Go back up">
    <i className="fa fa-level-up-alt" />
  </a>
);

const PieView = (
  props: ViewConfigT & { index: number, containerWidth: number }
) => {
  const nameAnchor = properDash(props.title, props.index);
  const meta = useContext(Context);
  const { user_can_see_private_data } = meta;
  // TODO: test
  if (props.private && !user_can_see_private_data) return null;

  const ahExists = () => props.activeHeader;
  const getCurrentHeader = compose(
    cond([[ahExists, ahExists], [T, head]]),
    getNumericKeys
  );
  const activeHeader = getCurrentHeader(props.headers);
  return (
    <div className={'pie-view ' + nameAnchor}>
      <h4>{props.title}</h4>
      {props.index > 0 && <BackBtn />}
      <section>
        <SectionTitle name={props.subtitle} />
        <Pie
          data={props.data}
          headers={props.headers}
          suffixes={props.suffixes}
          colors={getColors(props.data)}
          width={WIDGET_WIDTH}
          show={props.pie}
          activeHeader={activeHeader}
        />
        <SectionTable
          data={props.data}
          headers={props.headers}
          order={props.order}
          activeHeader={activeHeader}
        />
      </section>
    </div>
  );
};

const build = (config: ViewConfigT, i: number) => (
  <PieView key={i} {...config} index={i} />
);

function getParentUrl() {
  const isInIframe = parent !== window;
  let parentUrl = '';

  if (isInIframe) {
    parentUrl = document.referrer;
  }

  return parentUrl;
}

function inMemberPortal() {
  try {
    return getParentUrl().startsWith('https://community.');
  } catch (e) {
    console.error(e);
    return false;
  }
}

function TermsAndConditions() {
  // TODO: show loading
  // TODO: get the terms
  // TODO: handle errors
  return (
    <div>
      <h2>
        Release and Confidentiality Agreement for LICP Development Data
        Spreadsheet
      </h2>
      <p>
        This spreadsheet and the data it contains is provided to Members of the
        Long Island City Partnership (LICP) as a courtesy and is solely for your
        own use, and cannot be transferred or sold to a third party under any
        circumstances. You may incorporate the data into maps, marketing
        collateral and reports that you prepare for your corporate purposes,
        provided you credit LICP as follows:
      </p>

      <ul>
        <li>
          In text: “Source data provided by Long Island City Partnership.”
        </li>
        <li>
          On a map or visual: “
          <a href="https://files.constantcontact.com/d3254e99301/19d900ce-7596-4f2c-a414-89b87a0f1dc3.png?a=1128239631355">
            LICP LOGO
          </a>{' '}
          Source data provided by Long Island City Partnership.”
        </li>
      </ul>

      <p>
        Although the data information provided have been compiled and processed
        from sources believed to be reliable, no warranty, expressed or implied,
        is made regarding accuracy, adequacy, completeness, legality,
        reliability or usefulness of any data information. This disclaimer
        applies to both isolated and aggregate uses of the information. The
        information is provided on an "as is" basis.
      </p>

      <p>
        For a high-resolution version of the LICP logo please email
        ahoffman@licpartnership.org.
      </p>
    </div>
  );
}

export const DownloadBtn = (): null | Node => {
  const [isOpen, setModal] = useState(false);
  const [localErrors, setError] = useState([]);
  const { scheduleDownload, downloadErrors, isLoading } = useDownload();
  const errors = localErrors.concat(downloadErrors);

  // Only show this when the map is embedded in the member portal
  if (!__DEV__ && !inMemberPortal()) return null;
  const companyId = getAssetCompanyId();

  const getFilters = () => state('$query.filter', I.Map());
  const disabled = getFilters()
    .filter((_, k) => k === 'polygon')
    .isEmpty();

  function showModal() {
    setModal(true);
  }

  function onAccept() {
    // Call the backend with the current filters
    const filter = getFilters().toJS();
    scheduleDownload({ filter, company_id: companyId, isMember: true });
    setModal(false);
  }

  const onClick = (e) => {
    e && e.preventDefault();
    if (disabled) return;
    if (isLoading) return;
    setError([]);
    showModal();
  };

  const text = isLoading
    ? 'Preparing your download... (This could take some time.)'
    : 'Download records as CSV';

  return (
    <Fragment>
      <ErrorsView errors={errors} />
      <button
        css={css('font-size: 1.2rem;')}
        className="btn btn-link mb-4 btn-notransform"
        onClick={onClick}
        disabled={disabled}
      >
        {text}{' '}
        <i
          className={cx('fa', {
            'fa-download': !isLoading,
            'fa-spinner fa-spin': isLoading,
          })}
        />
      </button>
      <FreeModal
        id="tos"
        isOpen={isOpen}
        height="70vh"
        requestClosePortal={() => setModal(false)}
        title="Terms & Conditions"
        footer={
          <div>
            <button onClick={onAccept} className="btn btn-primary">
              I Agree
            </button>
          </div>
        }
      >
        <TermsAndConditions />
      </FreeModal>
    </Fragment>
  );
};

function Component(props: {
  data: ViewConfigArrT,
  grid?: boolean,
}): any | null {
  let data = [];
  let meta = { user_can_see_private_data: true };

  // For backwards compatibility.
  if (isArray(props.data)) {
    data = props.data || data;
  } else {
    data = props.data.data;
    meta = props.data.meta;
  }
  if (data.length === 0) return null;
  ThrowReporter.report(ViewConfigArr.decode(data));
  const className = cx('AnalyticsView ', { 'show-on-grid': props.grid });

  return (
    <Context.Provider value={meta}>
      <div
        className={className}
        css={css(`
        padding: 0 5px 140px;
        max-width: 100%;
        position: relative;

        .toc {
          select {
            border: solid 1px #ccc;
            margin-bottom: 20px;
            max-width: 370px;
          }

          h6 {
            float: left;
          }
        }

        .print-btn {
          float: right;
          margin-top: 3px;
        }

        &.show-on-grid {
          .print-btn {
            display: none;
          }
          .pies {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            grid-column-gap: 20px;
          }
        }

        .total-count-wrapper {
          position: absolute;
          left: calc(50% - 150px / 2);
          top: calc(50% - 150px / 2);
          width: 150px;
          height: 150px;
          display: grid;
          align-content: center;
          font-size: 3em;
          text-align: center;
          .small {
            font-size: 0.7em;
          }
        }

        .go-back {
          position: absolute;
          top: 0px;
          right: 0px;
        }
        .section-table {
          display: table;
          width: 100%;
          padding-bottom: 20px;
        }
        .section-row,
        .section-header {
          display: table-row;
        }
        .section-header {
          font-weight: bold;
          vertical-align: bottom;
          border-bottom: 2px solid #ddd;
        }
        .pie-view {
          background-color: #fff;
          border-radius: 10px;
          margin-bottom: 15px;
          padding: 10px;
          position: relative;
        }
        .pie-chart {
          margin: 0 auto;
          position: relative;
        }
        .pie-wrapper {
          position: relative;
        }
        .section-cell {
          display: table-cell;
          vertical-align: middle;
          width: 33%;
          padding: 8px;
          line-height: 1.42857;
          vertical-align: top;
          border-top: 1px solid #ddd;
        }
      `)}
      >
        <header className="text-right">
          <DownloadBtn data={data} />
        </header>
        <div className="pies">{data.map(build)}</div>
      </div>
    </Context.Provider>
  );
}

export default Component;
